import { actions as usersActions } from '@/api/users';
import { useAccountUsers } from '@/api/queries/me/useAccountUsers';
import { useActiveAccountUsers } from '@/api/queries/me/useActiveAccountUsers';
import { usePermissions } from '@/hooks/usePermissions';
import { DateInput } from '@payaca/ambrosia';
import CurrencyField from '@payaca/components/currencyField/CurrencyField';
import DropdownField from '@payaca/components/dropdownField/DropdownField';
import DurationInputControl from '@payaca/components/durationInputControl/DurationInputControl';
import FieldLabel from '@payaca/components/fieldLabel/FieldLabel';

import TextareaField from '@payaca/components/textareaField/TextareaField';
import Tooltip from '@payaca/components/tooltip/Tooltip';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { getTimelogTypes } from '@payaca/store/timelogs/timelogsActions';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { PublicTimelogType } from '@payaca/types/timelogs';
import UntitledIcon from '@payaca/untitled-icons';
import {
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import './PersistTimelogFields.sass';

type Props = {
  formState: {
    [key: string]: any;
  };
  validationState: {
    [key: string]: FieldValidationResult;
  };
  touchedState: {
    [key: string]: boolean;
  };
  onFieldChange: (value: { [key: string]: any }) => void;
  onFieldTouch: (fieldName: string) => void;
  persistType: 'create' | 'update';
};

const PersistTimelogFields: FunctionComponent<Props> = ({
  formState,
  validationState,
  touchedState,
  onFieldChange,
  onFieldTouch,
  persistType,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [timelogTypes, setTimelogTypes] = useState<PublicTimelogType[]>([]);
  const accountUsers = useAccountUsers();
  const activeAccountUsers = useActiveAccountUsers();
  const { userHasAllRequiredPermissions } = usePermissions();

  useEffect(() => {
    dispatch(usersActions.getAccountUsers());
    dispatch(
      getTimelogTypes.request({
        callback: (tt) => {
          setTimelogTypes(tt);
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const userAssignmentOptions = useMemo(() => {
    const options: { label: string; value: number }[] = activeAccountUsers.map(
      (accountUser: any) => {
        return {
          label: `${accountUser.firstname} ${accountUser.lastname}`,
          value: accountUser.id.toString(),
        };
      }
    );

    return options;
  }, [activeAccountUsers]);

  return (
    <div className="persist-timelog-fields">
      <ValidatedFieldWrapper
        validationResult={validationState['startTime']}
        isTouched={touchedState['startTime'] || false}
      >
        <div>
          <FieldLabel label="Start time" isRequired />
          <DateInput
            value={
              formState.startTime ? new Date(formState.startTime) : undefined
            }
            onChange={(change) => onFieldChange({ startTime: change })}
            showTimeSelect
            timeSettings={{
              showAllMinuteOptions: true,
              showNonBusinessHours: true,
            }}
            sizeVariant="sm"
          />
        </div>
      </ValidatedFieldWrapper>
      {formState.startTime && (
        <DurationInputControl
          name="duration"
          value={formState.duration}
          onChange={onFieldChange}
          onTouch={onFieldTouch}
          label={'Duration'}
        />
      )}

      <PermissionGuard
        renderIfHasPermissions={[
          persistType === 'create'
            ? TimelogsPermissions.CREATE_TIMELOGS
            : TimelogsPermissions.UPDATE_TIMELOGS,
        ]}
      >
        <ValidatedFieldWrapper
          validationResult={validationState['assigneePublicId']}
          isTouched={touchedState['assigneePublicId'] || false}
        >
          <DropdownField
            value={formState.assigneePublicId || []}
            name="assigneePublicId"
            label="Assignee"
            options={userAssignmentOptions}
            onChange={({ assigneePublicId }) => {
              const assignee = accountUsers.find(
                (x) => x.id === +assigneePublicId
              );

              onFieldChange({
                assigneePublicId,
                assigneeType: 'user',
                ...(userHasAllRequiredPermissions([
                  TimelogsPermissions.SET_TIMELOG_COST_PER_HOUR,
                  AccountsPermissions.GET_USER_COST_PER_HOUR,
                ])
                  ? { costPerHour: assignee?.costPerHour }
                  : {}),
              });
            }}
            onTouch={onFieldTouch}
          />
        </ValidatedFieldWrapper>
      </PermissionGuard>
      <PermissionGuard
        renderIfHasPermissions={[TimelogsPermissions.SET_TIMELOG_COST_PER_HOUR]}
      >
        <ValidatedFieldWrapper
          validationResult={validationState['costPerHour']}
          isTouched={touchedState['costPerHour'] || false}
        >
          <CurrencyField
            name="costPerHour"
            value={formState.costPerHour}
            onChange={onFieldChange}
            onTouch={onFieldTouch}
            label="Cost per hour"
          />
        </ValidatedFieldWrapper>
      </PermissionGuard>
      {!!timelogTypes?.length && (
        <PermissionGuard
          renderIfHasPermissions={[TimelogsPermissions.GET_TIMELOG_TYPES]}
        >
          <ValidatedFieldWrapper
            validationResult={validationState['typePublicId']}
            isTouched={touchedState['typePublicId'] || false}
          >
            <DropdownField
              value={formState.typePublicId || []}
              name="typePublicId"
              label="Type"
              options={timelogTypes.map((timelogType) => {
                return {
                  label: timelogType.type,
                  value: timelogType.publicId,
                };
              })}
              renderOption={(option) => {
                const type = timelogTypes.find(
                  (x) => x.publicId == option.value
                );
                return (
                  <div className="timelog-types-dropdown-option">
                    <span>{option.label}</span>

                    {!!type?.description?.length && (
                      <span className="description">
                        <Tooltip text={type.description}>
                          <UntitledIcon name="info-circle.3" />
                        </Tooltip>
                      </span>
                    )}
                  </div>
                );
              }}
              onChange={onFieldChange}
              onTouch={onFieldTouch}
            />
          </ValidatedFieldWrapper>
        </PermissionGuard>
      )}
      <ValidatedFieldWrapper
        validationResult={validationState['notes']}
        isTouched={touchedState['notes'] || false}
      >
        <TextareaField
          name="notes"
          value={formState.notes || ''}
          onChange={onFieldChange}
          onTouch={onFieldTouch}
          label="Notes"
        />
      </ValidatedFieldWrapper>
    </div>
  );
};

export default PersistTimelogFields;
